import type { KeyValue } from '../../net/progress';

/** Réglages audio du joueur : volumes de 0 à 1 et coupure globale, mémorisés en localStorage. */
export interface AudioSettings { music: number; sfx: number; muted: boolean; }

export const AUDIO_SETTINGS_KEY = 'rogfus.audio';
export const DEFAULT_AUDIO_SETTINGS: AudioSettings = { music: 0.6, sfx: 0.8, muted: false };

/** Ramène une valeur lue (JSON, curseur) dans [0, 1] ; `fallback` si ce n'est pas un nombre fini. */
export function clamp01(value: unknown, fallback: number): number {
  return typeof value === 'number' && Number.isFinite(value) ? Math.min(1, Math.max(0, value)) : fallback;
}

/** Lit des réglages sérialisés ; toute valeur absente ou invalide reprend le défaut (jamais d'exception). */
export function parseAudioSettings(raw: string | null): AudioSettings {
  if (!raw) return { ...DEFAULT_AUDIO_SETTINGS };
  try {
    const o = JSON.parse(raw) as Partial<Record<keyof AudioSettings, unknown>> | null;
    if (!o || typeof o !== 'object') return { ...DEFAULT_AUDIO_SETTINGS };
    return { music: clamp01(o.music, DEFAULT_AUDIO_SETTINGS.music), sfx: clamp01(o.sfx, DEFAULT_AUDIO_SETTINGS.sfx), muted: o.muted === true };
  } catch {
    return { ...DEFAULT_AUDIO_SETTINGS };
  }
}

export const serializeAudioSettings = (s: AudioSettings): string => JSON.stringify({ music: s.music, sfx: s.sfx, muted: s.muted });
export const loadAudioSettings = (kv: KeyValue): AudioSettings => parseAudioSettings(kv.get(AUDIO_SETTINGS_KEY));
export const saveAudioSettings = (kv: KeyValue, s: AudioSettings): void => kv.set(AUDIO_SETTINGS_KEY, serializeAudioSettings(s));

/** Volume effectif d'un son : gain du catalogue × réglage du joueur, 0 si muet. */
export function effectiveVolume(base: number, setting: number, muted: boolean): number {
  if (muted) return 0;
  return Math.min(1, Math.max(0, base * setting));
}
